/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/productVAT.scss';
import {
    AwesomeIcon, ChangeFunction, ChangeKeyFunction, CloseButton, ElementPosition, ErrorType, WithBackground
} from '@dvrd/dvr-controls';
import classNames from 'classnames';

import React, {MouseEventHandler, ReactElement, useMemo} from 'react';
import BolProduct from '../../../../models/bolProductModel';
import {getCountryLabel, getVatItemsByCountry} from '../../../../util/countryUtil';
import {BolmateSelectItem, SplitVATValue, VATPercentage, VATType, VATValue} from '../../../../util/interfaces';
import {translateVatValue, VATTypesMatch, VATValuesMatch} from '../../../../util/vatUtil';
import {BolmateButton, BolmateInput, BolmateNumberInput, BolmateSelect} from '../../../controls';
import IconWithLabel from "../../../icon/iconWithLabel";

interface Props {
    onClose: MouseEventHandler;
    onSubmit: MouseEventHandler;
    onChangeTitle: ChangeFunction<string>;
    onResetTitle: MouseEventHandler;
    onClickChange: (countryCode: string, value: VATValue) => MouseEventHandler;
    onChange: ChangeKeyFunction<string, number | VATValue | string>;
    onAddCountry: ChangeFunction<string>;
    onDeleteCountry: (countryCode: string) => MouseEventHandler;
    onChangeSplitPercentage: (countryCode: string, percentage: string) => ChangeFunction<string>;
    onChangeSplitValue: (countryCode: string, percentage: string) => ChangeFunction<number | string>;
    product: BolProduct;
    loading: boolean;
    active: boolean;
    errors: Record<string, ErrorType>;
    availableCountries: Array<[string, string]>;
}

function isCustomVAT(value: VATValue | null, options: Array<VATValue>): boolean {
    if (!value || ![VATType.DEFAULT, VATType.CUSTOM].includes(value.vat_type))
        return false;
    if (value.vat_type === VATType.CUSTOM) return true;
    return !options.find((option: VATValue) => VATValuesMatch(value, option));
}

const percentages = [
    VATPercentage.BE_LOW,
    VATPercentage.NL_LOW,
    VATPercentage.BE_MID,
    VATPercentage.DE_HIGH,
    VATPercentage.HIGH
];

function getAvailableSplitPercentages(currentPercentage: number, usedPercentages: Array<string>): Array<number> {
    return [currentPercentage].concat(
        percentages.filter((perc: number) => !usedPercentages.includes(perc.toString()))).toSorted(
        (percA: number, percB: number) => percA - percB);
}

export default function ProductVAT(props: Props) {
    const {
        onClose, onSubmit, onChangeTitle, onClickChange, onChange, onAddCountry, onDeleteCountry,
        onChangeSplitPercentage, onChangeSplitValue, product, loading, active, errors, availableCountries, onResetTitle
    } = props;
    const [editingTitle, setEditingTitle] = React.useState(false);
    const countryItems: Array<BolmateSelectItem> = useMemo(() => {
        const items: Array<BolmateSelectItem> = [{value: '', label: 'Kies een land', selectable: false}];
        return items.concat(
            availableCountries.map((entry: [string, string]) => ({value: entry[0], label: entry[1]})));
    }, [availableCountries]);

    function onEnterTitle() {
        setEditingTitle(false);
    }

    function onEditTitle() {
        setEditingTitle(true);
    }

    function renderHeader() {
        return (
            <div className='header-container'>
                {renderImage()}
                {renderProductDetails()}
                <CloseButton onClick={onClose}/>
            </div>
        );
    }

    function renderImage() {
        let image: ReactElement<any>;
        if (!!product.imageURL) image = <img src={product.imageURL} className='product-image' alt=''/>;
        else image = <AwesomeIcon name='image' className='image-icon'/>;
        return (
            <div className='product-image-container'>
                {image}
            </div>
        );
    }

    function renderProductDetails() {
        return (
            <div className='product-details'>
                {editingTitle ? <div className='title-row'>
                        <BolmateInput onChange={onChangeTitle} value={product.actualTitle} onEnter={onEnterTitle}
                                      className='product-title-field' autoFocus autoSelect/>
                        <IconWithLabel label='Gebruik Bol naam' name='arrows-rotate' onClick={onResetTitle}
                                       className='btn-title-reset click'/>
                    </div>
                    : <div className='product-title-container'>
                        <label className='product-title'>{product.actualTitle}</label>
                        <AwesomeIcon name='edit' onClick={onEditTitle} className='btn-edit-title'/>
                    </div>}
                <label className='ean'>EAN: {product.ean}</label>
            </div>
        );
    }

    function renderBody() {
        return (
            <div className='body-container'>
                <label>Afwijkende BTW tarieven instellen</label>
                {!product.vats.length ?
                    <label className='default-vat'>Geen afwijkend BTW tarief ingesteld. Gebruikt standaard
                        tarief.</label> : <div className='countries'>
                        {product.vats.sortedCountryCodes.map(renderCountry)}
                    </div>}
                {renderAddCountry()}
            </div>
        );
    }

    function renderCountry(countryCode: string, index: number) {
        const options = getVatItemsByCountry(countryCode);
        const currentValue = product.vats.getCountryValue(countryCode);
        const isCustom = isCustomVAT(currentValue, options);
        const isSplit = currentValue?.vat_type === VATType.SPLIT;
        const hasError = !!errors[countryCode];
        return (
            <div key={index} className='country-product-vat'>
                {!!errors[countryCode] && <label className='error-label'>{errors[countryCode]}</label>}
                <div className='vat-row'>
                    <label className={classNames('country-label', hasError && 'error')}>{getCountryLabel(
                        countryCode)}</label>
                    <div className='vat-options'>
                        {options.map(renderCountryValue(countryCode))}
                    </div>
                    <AwesomeIcon name='trash-alt' onClick={onDeleteCountry(countryCode)} className='btn-delete'/>
                </div>
                {isCustom && (
                    <BolmateNumberInput onChange={onChange(countryCode)} value={currentValue!.value}
                                        label='Aangepast tarief' className='custom-vat-field'
                                        ornaments={{element: <span>%</span>, placement: ElementPosition.RIGHT}}
                                        asNumber wholeNumbers disableSymbols autoFocus autoSelect/>
                )}
                {isSplit && renderSplitVAT(countryCode, currentValue as SplitVATValue)}
            </div>
        );
    }

    function renderSplitVAT(countryCode: string, vatValue: SplitVATValue | null) {
        const entries: Array<[string, number]> = Object.entries(vatValue?.value ?? {'9': 0, '21': 0}).slice(0, 2);
        if (!entries.length) entries.push(['9', 0], ['21', 0]);
        return (
            <div className='split-vat-container'>
                {entries.map(renderSplitVATEntry(countryCode))}
            </div>
        );
    }

    function renderSplitVATEntry(countryCode: string) {
        return function(entry: [string, number], index: number, allEntries: Array<[string, number]>) {
            const percentages = getAvailableSplitPercentages(Number(entry[0]), allEntries.map((entry) => entry[0]));
            const percentageItems: Array<BolmateSelectItem> = [{
                value: '', label: 'Kiezen...', selectable: false
            } as BolmateSelectItem].concat(
                percentages.map((percentage: number) => ({label: `${percentage}%`, value: percentage.toString()})));
            return (
                <div key={index} className='split-vat-entry'>
                    <BolmateSelect items={percentageItems} value={entry[0]}
                                   onChange={onChangeSplitPercentage(countryCode, entry[0])} label='BTW percentage'/>
                    <BolmateNumberInput onChange={onChangeSplitValue(countryCode, entry[0])} value={entry[1]}
                                        ornaments={{element: <span>&euro;</span>}} label='BTW bedrag per stuk'
                                        asCurrency autoSelect/>
                </div>
            );
        };
    }

    function renderCountryValue(countryCode: string) {
        return function(value: VATValue, index: number, options: Array<VATValue>) {
            let countryValue = product.vats.getCountryValue(countryCode);
            let active: boolean;
            if (isCustomVAT(countryValue, options)) active = value.vat_type === VATType.CUSTOM;
            else if (countryValue?.vat_type === VATType.DEFAULT) active = VATValuesMatch(countryValue, value);
            else active = VATTypesMatch(countryValue, value);
            return (
                <div key={index} className={classNames('vat-value', active && 'active')}
                     onClick={onClickChange(countryCode, value)}>
                    <label>{translateVatValue(value, undefined, {fullLabels: true})}</label>
                </div>
            );
        };
    }

    function renderAddCountry() {
        return (
            <div className='add-country'>
                <label>Afwijkend BTW tarief toevoegen voor:</label>
                <BolmateSelect items={countryItems} onChange={onAddCountry} value='' itemsHeight='20rem'
                               itemsPosition='top'/>
            </div>
        );
    }

    return (
        <WithBackground active={active} onClose={onClose} preventBackgroundClick>
            <div className='default-popup product-vat-settings'>
                {renderHeader()}
                {renderBody()}
                <div className='footer-container btn-2'>
                    <BolmateButton onClick={onClose} label='Annuleren' disabled={loading} secondary/>
                    <BolmateButton onClick={onSubmit} label='Opslaan' disabled={loading}/>
                </div>
            </div>
        </WithBackground>
    );
}